import {
    CalculatorIcon,
    DatabaseIcon,
    FileTextIcon,
    InfoIcon,
    TriangleAlertIcon,
} from "lucide-react";

import {Badge} from "@components/ui/badge";
import {Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle} from "@components/ui/sheet";

import type {SaaSMetric} from "./saasMockData";

interface SaaSMetricDetailSheetProps {
    metric: SaaSMetric | null;
    monthLabel: string;
    onOpenChange: (open: boolean) => void;
}

function SectionHeading({icon, children}: {icon: React.ReactNode; children: React.ReactNode}) {
    return (
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[1.5px] text-[#209DB3]">
            {icon}
            {children}
        </div>
    );
}

/**
 * Read-only drill-down drawer for a single SaaS metric.
 * Mirrors the Workbench right-side Sheet pattern: shows the result, how it was
 * produced (derived → formula + worked calc; imported → source row, no fabricated
 * derivation), the inputs, source provenance, and any interpretation caveats.
 */
export function SaaSMetricDetailSheet({metric, monthLabel, onOpenChange}: SaaSMetricDetailSheetProps) {
    const detail = metric?.detail;
    const isDerived = detail?.method === "derived";

    return (
        <Sheet open={Boolean(metric)} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg">
                {metric && detail && (
                    <>
                        <SheetHeader className="border-b bg-slate-50/70 p-5">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge
                                    variant="outline"
                                    className={
                                        isDerived
                                            ? "border-[#209DB3]/40 bg-[#209DB3]/10 text-[#1a8c9e]"
                                            : "border-indigo-300 bg-indigo-50 text-indigo-700"
                                    }
                                >
                                    {isDerived ? "Derived" : "Imported"}
                                </Badge>
                                <Badge variant="outline" className="border-slate-300 bg-white text-slate-600">
                                    {detail.basis}
                                </Badge>
                            </div>
                            <SheetTitle className="mt-1 text-lg text-slate-900">{metric.label}</SheetTitle>
                            <SheetDescription className="flex items-baseline gap-2">
                                <span className="text-2xl font-extrabold tracking-tight text-[#1a8c9e] tabular-nums">
                                    {metric.display}
                                </span>
                                <span className="text-xs text-slate-500">{monthLabel}</span>
                            </SheetDescription>
                        </SheetHeader>

                        <div className="flex flex-col gap-6 p-5">
                            {/* Definition */}
                            <section className="flex flex-col gap-2">
                                <SectionHeading icon={<InfoIcon className="size-3.5" />}>Definition</SectionHeading>
                                <p className="text-sm leading-relaxed text-slate-600">{detail.definition}</p>
                            </section>

                            {/* How it was calculated */}
                            <section className="flex flex-col gap-2">
                                <SectionHeading icon={<CalculatorIcon className="size-3.5" />}>
                                    {isDerived ? "How it was calculated" : "How it is sourced"}
                                </SectionHeading>
                                {isDerived ? (
                                    <div className="flex flex-col gap-2">
                                        {detail.formula && (
                                            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[13px] text-slate-700">
                                                {detail.formula}
                                            </div>
                                        )}
                                        {detail.calculation && (
                                            <div className="rounded-md border border-[#209DB3]/30 bg-[#209DB3]/5 px-3 py-2 font-mono text-[13px] text-[#1a8c9e]">
                                                {detail.calculation}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        {detail.sourceRow && (
                                            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                                                <span className="text-xs font-medium text-slate-500">Source row</span>
                                                <span className="font-mono text-[13px] text-slate-700">
                                                    {detail.sourceRow}
                                                </span>
                                            </div>
                                        )}
                                        <p className="text-sm leading-relaxed text-slate-600">
                                            Imported directly from the source snapshot; no Pecunio transformation
                                            applied.
                                        </p>
                                    </div>
                                )}
                            </section>

                            {/* Inputs (derived only) */}
                            {isDerived && detail.inputs && detail.inputs.length > 0 && (
                                <section className="flex flex-col gap-2">
                                    <SectionHeading icon={<FileTextIcon className="size-3.5" />}>Inputs</SectionHeading>
                                    <div className="overflow-hidden rounded-md border border-slate-200">
                                        <table className="w-full text-[13px]">
                                            <tbody>
                                                {detail.inputs.map((input, i) => (
                                                    <tr
                                                        key={input.label}
                                                        className={i % 2 === 1 ? "bg-slate-50/70" : "bg-white"}
                                                    >
                                                        <td className="px-3 py-2 text-slate-600">{input.label}</td>
                                                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-800">
                                                            {input.value}
                                                        </td>
                                                        <td className="px-3 py-2 text-right text-[11px] text-slate-400">
                                                            {input.sourceLabel}
                                                            {input.sourcePeriod ? ` · ${input.sourcePeriod}` : ""}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            )}

                            {/* Source */}
                            <section className="flex flex-col gap-2">
                                <SectionHeading icon={<DatabaseIcon className="size-3.5" />}>Source</SectionHeading>
                                <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-[13px]">
                                    <div className="font-medium text-slate-700">{detail.source}</div>
                                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
                                        {detail.importedAt && <span>Imported {detail.importedAt}</span>}
                                        {detail.schemaVersion != null && <span>Schema v{detail.schemaVersion}</span>}
                                    </div>
                                </div>
                                {detail.benchmark && (
                                    <p className="text-xs italic text-slate-500">{detail.benchmark}</p>
                                )}
                            </section>

                            {/* Caveats */}
                            {detail.caveats && detail.caveats.length > 0 && (
                                <section className="flex flex-col gap-2">
                                    <SectionHeading icon={<TriangleAlertIcon className="size-3.5" />}>Notes</SectionHeading>
                                    <ul className="flex flex-col gap-1.5">
                                        {detail.caveats.map((c) => (
                                            <li
                                                key={c}
                                                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800"
                                            >
                                                {c}
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
